import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer, invalidateCartRead } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
const companionDebug = (...args) => console.warn('[cart-items]', ...args);

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #lastKiteCartSignature = null;
  #isCartUpdatePending = false;

  #getRowByCartLine(line) {
    const input = this.querySelector(`input[data-cart-line="${line}"]`);
    return input ? input.closest('tr.cart-items__table-row') : null;
  }

  #getCompanionVariantIds() {
    const config = window.themeCartCompanion || {};
    if (!config.enabled) return [];
    return (config.variantIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  }

  #getCompanionCleanupFromItems(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return { shouldCleanup: false, updates: {} };
    }

    const paidItems = items.filter((item) => Number(item?.final_line_price) > 0);
    const freeItems = items.filter((item) => Number(item?.final_line_price) === 0);

    if (paidItems.length > 0 || freeItems.length === 0) {
      return { shouldCleanup: false, updates: {} };
    }

    const updates = {};
    freeItems.forEach((item) => {
      if (item?.key) updates[item.key] = 0;
    });

    return { shouldCleanup: Object.keys(updates).length > 0, updates };
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('submit', this.#handlePendingCartSubmit, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('submit', this.#handlePendingCartSubmit, true);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getRowByCartLine(line) || this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    event?.preventDefault();
    if (this.#isCartUpdatePending) return;
    const clickedRow = event?.target instanceof Element ? event.target.closest('tr[data-key]') : null;
    const cartItemRowToRemove = clickedRow && this.contains(clickedRow) ? clickedRow : this.#getRowByCartLine(line);
    if (!cartItemRowToRemove?.dataset.key) return;

    // Keep the row visible and show a spinner on its remove button until the server confirms the
    // deletion. The row is only removed when the cart section is re-rendered (morphSection), so the
    // cart never looks emptier than it actually is before the request completes (e.g. at checkout).
    if (cartItemRowToRemove) {
      const rowsToMark = [
        cartItemRowToRemove,
        // Include any nested lines of the row being removed.
        ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ];
      rowsToMark.forEach((row) => row.classList.add('cart-items__table-row--removing'));
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      row: cartItemRowToRemove,
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.#isCartUpdatePending) return;
    const { line, quantity } = config;
    const row = config.row || this.#getRowByCartLine(line);
    const rowKey = row?.dataset?.key;
    if (!rowKey || !Number.isInteger(Number(line)) || Number(line) < 1) return;
    const previousQuantity = Number(row.querySelector('input[data-cart-line]')?.defaultValue);
    this.#isCartUpdatePending = true;
    this.dataset.cartUpdatePending = 'true';
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    let requestUrl = Theme.routes.cart_change_url;
    let body;

    // Remove companion items in the same request when deleting the last regular product.
    if (quantity === 0) {
      companionDebug('remove:start', { line, quantity });
      const companionVariantIds = this.#getCompanionVariantIds();
      const rowVariantId = Number(row?.querySelector('[data-variant-id]')?.getAttribute('data-variant-id'));

      if (companionVariantIds.length > 0 && rowKey && !companionVariantIds.includes(rowVariantId)) {
        const companionSet = new Set(companionVariantIds);
        const rows = this.refs.cartItemRows || [];

        const regularRowsCount = rows.filter((cartRow) => {
          const variantId = Number(cartRow.querySelector('[data-variant-id]')?.getAttribute('data-variant-id'));
          return Number.isFinite(variantId) && !companionSet.has(variantId);
        }).length;

        if (regularRowsCount <= 1) {
          const updates = { [rowKey]: 0 };
          companionVariantIds.forEach((id) => {
            updates[id] = 0;
          });

          requestUrl = Theme.routes.cart_update_url;
          body = JSON.stringify({
            updates,
            sections,
            sections_url: window.location.pathname,
          });
          companionDebug('remove:lastRegularWithCompanions', { line, rowKey, updates });
        }
      }
    }

    if (!body) {
      body = JSON.stringify({
        id: rowKey,
        quantity: quantity,
        sections,
        sections_url: window.location.pathname,
      });
    }
    companionDebug('request', { requestUrl, body: JSON.parse(body) });

    cartTotal?.shimmer();

    invalidateCartRead();
    fetch(`${requestUrl}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        invalidateCartRead();
        let parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        if (quantity === 0) {
          const cleanup = this.#getCompanionCleanupFromItems(parsedResponseText.items);
          if (cleanup.shouldCleanup) {
            companionDebug('cleanup:onlyCompanionsRemain', { updates: cleanup.updates });
            const cleanupPayload = {
              updates: cleanup.updates,
              sections,
              sections_url: window.location.pathname,
            };
            const cleanupResponse = await fetch(
              `${Theme.routes.cart_update_url}`,
              fetchConfig('json', { body: JSON.stringify(cleanupPayload) })
            );
            parsedResponseText = await cleanupResponse.json();
          }
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            changedLineKey: rowKey,
            quantityDelta: (parsedResponseText.items.find((item) => item.key === rowKey)?.quantity || 0) - previousQuantity,
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#isCartUpdatePending = false;
        delete this.dataset.cartUpdatePending;
        this.#enableCartItems();
        // Clear any pending-removal spinner. On success the rows are already gone (section was
        // morphed); on error/early-return they persist and must be reset so the spinner stops.
        this.querySelectorAll('.cart-items__table-row--removing').forEach((row) => {
          row.classList.remove('cart-items__table-row--removing');
        });
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  #handlePendingCartSubmit = (event) => {
    if (!this.#isCartUpdatePending || !(event.target instanceof HTMLFormElement) || !this.contains(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.#getRowByCartLine(line)?.querySelector('input[data-cart-line]');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    const resource = event.detail?.resource;
    if (event.detail?.data?.source === 'kite' && Array.isArray(resource?.items)) {
      const signature = JSON.stringify([resource.items, resource.total_price, resource.cart_level_discount_applications]);
      if (signature === this.#lastKiteCartSignature) return;
      this.#lastKiteCartSignature = signature;
    } else {
      this.#lastKiteCartSignature = null;
    }
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (event.detail.data.source === 'cart-gift-countdown') {
      sectionRenderer.updateSection(this.sectionId, cartItemsHtml)
        .then(() => this.#updateCartQuantitySelectorButtonStates())
        .catch((error) => console.error('[cart-items] Could not refresh gift items.', error));
      return;
    }
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
